import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { CreditCard, Shield, Clock, Check, Sparkles, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../utils/api';

const TrialSetupPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { state: { from: '/setup-trial' } });
    }
  }, [user, authLoading, navigate]);

  // Check if user already has payment set up
  useEffect(() => {
    if (user) {
      // If user has a Stripe subscription or is free_for_life, redirect to dashboard
      if (user.stripe_subscription_id || user.subscription_status === 'free_for_life' || user.subscription_status === 'active') {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  // Poll for payment completion
  useEffect(() => {
    if (sessionId) {
      pollTrialSetupStatus(sessionId);
    }
  }, [sessionId]);

  const pollTrialSetupStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      toast.error('Status check timed out. Please refresh the page.');
      setCheckingStatus(false);
      return;
    }

    setCheckingStatus(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/subscription/trial-status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();

      if (data.status === 'success') {
        toast.success('Payment method saved! Your trial is now active.');
        await refreshUser();
        setCheckingStatus(false);
        navigate('/dashboard', { replace: true });
        return;
      }

      // Continue polling
      setTimeout(() => pollTrialSetupStatus(sessionId, attempts + 1), pollInterval);
    } catch (err) {
      console.error('Error checking trial status:', err);
      setTimeout(() => pollTrialSetupStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  const handleSetupPayment = async () => {
    if (!user) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/subscription/setup-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          origin_url: window.location.origin,
          plan: 'monthly',
          promo_code: user.promo_code_used || null
        })
      });

      const data = await res.json();

      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (err) {
      console.error('Failed to setup trial payment', err);
      toast.error('Failed to start payment setup');
    } finally {
      setLoading(false);
    }
  };

  // Determine trial days based on user's promo code
  const getTrialDays = () => {
    if (user?.promo_code_used) {
      const promoDays = {
        '2MONTHFREE': 60,
        'LAUNCH6': 180,
        '2YEARS': 730,
        'VIP': 36500,
        'TRADIE20': 30,
        'EASTER26': 30,
        'EFCM26': 60
      };
      return promoDays[user.promo_code_used] || 30;
    }
    return 30;
  };

  // Check for discounted pricing
  const getPromoDetails = () => {
    const code = user?.promo_code_used;
    
    if (code === 'EASTER26') {
      return {
        discountedPrice: 10,
        discountMonths: 1,
        fullPrice: 30,
        description: 'Easter Special'
      };
    }
    
    if (code === 'EFCM26') {
      return {
        discountedPrice: 15,
        discountMonths: 2,
        fullPrice: 30,
        description: 'Special Offer'
      };
    }
    
    return null;
  };

  const trialDays = getTrialDays();
  const promoDetails = getPromoDetails();

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-2 border-teal-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (checkingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-teal-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center max-w-md">
          <div className="animate-spin h-12 w-12 border-3 border-teal-500 border-t-transparent rounded-full mx-auto mb-4" />
          <h2 className="text-xl font-bold text-stone-900 mb-2">Setting up your trial...</h2>
          <p className="text-stone-600">Please wait while we confirm your payment method.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 to-teal-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Sparkles className="h-4 w-4" />
            Welcome to Crafters Market!
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
            One Last Step
          </h1>
          <p className="text-stone-600 text-lg">
            Add your payment method to start your free trial
          </p>
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Trial Badge */}
          <div className="bg-gradient-to-r from-sky-500 to-teal-500 text-white p-6 text-center">
            <div className="flex items-center justify-center gap-3 mb-2">
              <Clock className="h-6 w-6" />
              <span className="text-2xl font-bold">{trialDays}-Day Free Trial</span>
            </div>
            <p className="text-sky-100">
              You won't be charged until {new Date(Date.now() + trialDays * 24 * 60 * 60 * 1000).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Benefits */}
            <div className="mb-8">
              <h3 className="font-semibold text-stone-900 mb-4">What you get:</h3>
              <ul className="space-y-3">
                {[
                  'Unlimited listings during trial',
                  'Direct messaging with buyers',
                  'Featured in local searches',
                  'No commission on sales - ever!',
                  'Cancel anytime before trial ends'
                ].map((benefit, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-stone-700">
                    <div className="w-5 h-5 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <Check className="h-3 w-3 text-teal-600" />
                    </div>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>

            {/* Security Note */}
            <div className="bg-stone-50 rounded-lg p-4 mb-6 flex items-start gap-3">
              <Shield className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-stone-600">
                <p className="font-medium text-stone-700 mb-1">Secure & Flexible</p>
                <p>Your card details are securely stored by Stripe. You can cancel anytime from your profile settings, and you won't be charged if you cancel before your trial ends.</p>
              </div>
            </div>

            {/* Price Info */}
            <div className="border border-stone-200 rounded-lg p-4 mb-6">
              {promoDetails ? (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <div>
                      <p className="font-medium text-stone-900">
                        {promoDetails.discountMonths === 1 ? 'First month after trial' : `First ${promoDetails.discountMonths} months after trial`}
                      </p>
                      <p className="text-sm text-green-600 font-medium">{promoDetails.description || 'Special Offer'} with {user?.promo_code_used}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-2xl font-bold text-green-600">${promoDetails.discountedPrice}<span className="text-base font-normal text-stone-500">/month</span></p>
                      <p className="text-sm text-stone-400 line-through">${promoDetails.fullPrice}/month</p>
                    </div>
                  </div>
                  <div className="border-t border-stone-200 pt-3 flex justify-between items-center text-sm text-stone-500">
                    <span>After {promoDetails.discountMonths === 1 ? '1 month' : `${promoDetails.discountMonths} months`}</span>
                    <span>${promoDetails.fullPrice}/month</span>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <div>
                    <p className="font-medium text-stone-900">After trial ends</p>
                    <p className="text-sm text-stone-500">Monthly subscription</p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-stone-900">$7.99<span className="text-base font-normal text-stone-500">/month</span></p>
                  </div>
                </div>
              )}
            </div>

            {/* CTA Button */}
            <Button
              onClick={handleSetupPayment}
              disabled={loading}
              className="w-full bg-gradient-to-r from-sky-500 to-teal-500 hover:from-sky-600 hover:to-teal-600 text-white py-6 text-lg font-semibold rounded-xl shadow-lg"
              data-testid="setup-payment-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                  Setting up...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  Add Payment Method
                  <ArrowRight className="h-5 w-5" />
                </span>
              )}
            </Button>

            <p className="text-center text-sm text-stone-500 mt-4">
              {promoDetails 
                ? `By continuing, you agree to be charged $${promoDetails.discountedPrice}/month for the first 6 months after your ${trialDays}-day trial, then $${promoDetails.fullPrice}/month unless you cancel.`
                : `By continuing, you agree to be charged $7.99/month after your ${trialDays}-day trial unless you cancel.`
              }
            </p>
          </div>
        </div>

        {/* Skip Option - Only for browsing */}
        <div className="text-center mt-6">
          <p className="text-stone-500 text-sm mb-2">Just want to browse?</p>
          <Link 
            to="/" 
            className="text-teal-600 hover:text-teal-700 font-medium text-sm"
          >
            Continue without selling privileges →
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TrialSetupPage;
